import { createTheme } from '@mui/material/styles';
import { colors, distances, border } from './constants';

export const theme = createTheme({
    palette: {
        mode: 'light',
        background: {
            default: colors.backgroundAlt,
            paper: colors.background,
        },
        primary: {
            main: colors.primary,
        },
        secondary: {
            main: colors.secondary.child,
        },
        text: {
            primary: colors.text,
            secondary: colors.textSecondary,
        },
        action: {
            disabled: colors.primaryDisabled,
            focus: colors.primaryFocusOutline,
            hover: colors.primaryHover,
            disabledBackground: colors.backgroundAlt,
        },
        error: {
            main: colors.invalid,
        },
        success: {
            main: colors.valid,
        },
        divider: colors.borderLight,
    },
    shape: {
        borderRadius: 4,
    },
    spacing: 8, // same as distances.tiny
    typography: {
        fontFamily: 'Aeonik Pro, -apple-system BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol',
        fontSize: 14,
        allVariants: {
            color: colors.text,
            fontWeight: 400,
            fontStyle: 'normal',
            fontStretch: 'normal',
        },
        // from components/Typography
        h1: {
            color: colors.primary,
            fontSize: '30px',
            fontWeight: 700,
            lineHeight: '48px',
            margin: 0,
        },
        h2: {
            fontSize: '23px',
            fontWeight: 700,
            lineHeight: distances.normal,
            margin: 0,
        },
        h3: {
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: distances.normal,
            margin: 0,
        },
        h4: {
            fontSize: '12px',
            fontWeight: 'normal',
            lineHeight: distances.normal,
            margin: 0,
            textTransform: 'uppercase',
        },
        // todo, h5, h6, label, text ?
    },
    components: {
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    color: colors.text,
                    fontWeight: 700,
                    transform: 'translate(0, -1.5px)',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                sizeSmall: {
                    fontSize: '12px',
                },
                root: {
                    fontSize: '14px',
                    color: colors.text,
                    fontWeight: 700,
                    transform: 'translate(0, -1.5px)',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    padding: `${distances.tiny} 0`,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    // Seems like autocomplete has some additional padding which is exactly 4 px
                    padding: `${distances.micro} 0`,
                },
            },
            defaultProps: {
                disablePortal: true,
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiInput: {
            styleOverrides: {
                input: {
                    ':focus': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
                root: {
                    ':before': {
                        borderBottom: `${border.normal} solid ${colors.borderLight}`,
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                input: {
                    backgroundColor: colors.backgroundAlt,
                    ':focus': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
                root: {
                    ':before': {
                        borderBottom: `${border.normal} solid ${colors.borderLight}`,
                    },
                },
            },
        },
        // TODO: Fix outlined input, looks kinda broken now
        MuiSelect: {
            defaultProps: {
                variant: 'standard',
            },
            styleOverrides: {
                standard: {
                    ':focus': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
                filled: {
                    ':focus': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
                outlined: {
                    ':focus': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: colors.borderLight,
                    borderBottomWidth: border.normal,
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                // Because of the <Root> wrapping component in App,
                // which applies some general Css, we can't portal the dialogs to the document,
                // otherwise any non-mui components in the dialog won't get correct css
                disablePortal: true,
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: `${distances.normal} ${distances.normal} ${distances.small} ${distances.normal}`,
                    fontWeight: 700,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: distances.normal,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    padding: `0 ${distances.normal} ${distances.normal} ${distances.normal}`,
                },
            },
        },
        MuiDialogContentText: {
            defaultProps: {
                color: 'textPrimary',
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ':hover': {
                        backgroundColor: colors.interactiveBackgroundHover,
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&.MuiTableRow-hover': {
                        '&:hover': {
                            backgroundColor: `${colors.interactiveBackgroundHover}`,
                        },
                    },
                },
            },
        },
        MuiTooltip: { styleOverrides: {
            tooltip: {
                fontSize: '16px',
                color: '#fff',
                backgroundColor: '#212121',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
            arrow: {
                color: '#212121',
            },
        },
        },
    },
});
